import { APP_VERSION } from '../version';
import { logger } from '../utils/logger';

export interface VersionInfo {
  version: string;
  hash: string;
  buildTime: string;
  lastUpdated: string;
}

export interface UpdateCheckResult {
  hasUpdate: boolean;
  currentVersion: string;
  latestVersion: string;
  currentHash: string;
  latestHash: string;
  lastChecked: Date;
}

class UpdateCheckService {
  private lastCheck: Date | null = null;
  private checkInterval: number = 5 * 60 * 1000; // 5 minutos
  private endpointUrl: string = '/version.json';

  /**
   * Verifica si hay una nueva versión disponible
   */
  async checkForUpdates(): Promise<UpdateCheckResult> {
    try {
      // Evitar múltiples checks simultáneos
      if (this.lastCheck && Date.now() - this.lastCheck.getTime() < this.checkInterval) {
        return {
          hasUpdate: false,
          currentVersion: APP_VERSION.version,
          latestVersion: APP_VERSION.version,
          currentHash: APP_VERSION.hash,
          latestHash: APP_VERSION.hash,
          lastChecked: this.lastCheck
        };
      }

      this.lastCheck = new Date();

      // Obtener información de la versión actual
      const currentVersion = APP_VERSION.version;
      const currentHash = APP_VERSION.hash;

      // Obtener información de la versión más reciente desde el endpoint
      const response = await fetch(this.endpointUrl, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const latestVersionInfo: VersionInfo = await response.json();

      // Comparar versiones usando fechas de build
      const hasUpdate = this.compareVersions(currentHash, latestVersionInfo.hash, latestVersionInfo.buildTime);

      return {
        hasUpdate,
        currentVersion,
        latestVersion: latestVersionInfo.version,
        currentHash,
        latestHash: latestVersionInfo.hash,
        lastChecked: this.lastCheck
      };

    } catch (error) {
      logger.warn('UPDATE_SERVICE', 'Error al verificar actualizaciones:', error);
      
      // En caso de error, asumir que no hay actualización
      return {
        hasUpdate: false,
        currentVersion: APP_VERSION.version,
        latestVersion: APP_VERSION.version,
        currentHash: APP_VERSION.hash,
        latestHash: APP_VERSION.hash,
        lastChecked: this.lastCheck || new Date()
      };
    }
  }

  /**
   * Compara dos hashes de versión usando fechas de build
   * En desarrollo, solo mostrar actualización si la versión desplegada es más reciente
   */
  private compareVersions(currentHash: string, latestHash: string, latestBuildTime?: string): boolean {
    // Si los hashes son iguales, no hay actualización
    if (currentHash === latestHash) {
      return false;
    }

    // En desarrollo (localhost), usar lógica más inteligente
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      logger.log('UPDATE_SERVICE', '🔧 Modo desarrollo detectado');
      
      // Si tenemos fecha de build de la versión desplegada, comparar fechas
      if (latestBuildTime) {
        const currentBuildTime = APP_VERSION.buildTime;
        const latestBuildDate = new Date(latestBuildTime);
        const currentBuildDate = new Date(currentBuildTime);
        
        logger.log('UPDATE_SERVICE', '📅 Comparando fechas de build:');
        logger.log('UPDATE_SERVICE', `   - Actual (desarrollo): ${currentBuildTime}`);
        logger.log('UPDATE_SERVICE', `   - Desplegada: ${latestBuildTime}`);
        
        // Solo mostrar actualización si la versión desplegada es más reciente
        if (latestBuildDate > currentBuildDate) {
          logger.log('UPDATE_SERVICE', '✅ Versión desplegada es más reciente - mostrar actualización');
          return true;
        } else {
          logger.log('UPDATE_SERVICE', '❌ Versión actual es más reciente - no mostrar actualización');
          return false;
        }
      }
      
      // Si no tenemos fecha de build, no mostrar actualización en desarrollo
      logger.warn('UPDATE_SERVICE', '⚠️ No se puede determinar la fecha de build - no mostrar actualización en desarrollo');
      return false;
    }

    // En producción, si los hashes son diferentes, hay una actualización
    logger.log('UPDATE_SERVICE', '🌐 Modo producción - mostrar actualización si hashes son diferentes');
    return true;
  }

  /**
   * Fuerza una nueva verificación ignorando el intervalo
   */
  async forceCheck(): Promise<UpdateCheckResult> {
    this.lastCheck = null;
    return this.checkForUpdates();
  }

  /**
   * Configura el intervalo de verificación
   */
  setCheckInterval(intervalMs: number): void {
    this.checkInterval = intervalMs;
  }

  /**
   * Configura la URL del endpoint
   */
  setEndpointUrl(url: string): void {
    this.endpointUrl = url;
  }
}

// Exportar instancia singleton
export const updateCheckService = new UpdateCheckService();
